import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Wrench,
  Users,
  Package2,
  FileCheck,
  Filter,
  Calendar,
  Building,
  Tag,
  Loader2,
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  Clock,
  LucideIcon,
  Sparkles,
  PenTool,
  Shield,
  Ticket,
  History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { ReportCard, ReportTable, ReportChart, ExportButton, InsightReports, CustomReportBuilder, ScheduledReports } from '../components';

interface ReportType {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
  endpoint: string;
  category?: 'standard' | 'legacy';
}

const reportTypes: ReportType[] = [
  {
    id: 'assets',
    title: 'Asset Summary',
    description: 'Total assets by status, category, branch with value summary',
    icon: FileText,
    color: 'bg-blue-500',
    endpoint: '/reports/assets',
    category: 'standard',
  },
  {
    id: 'utilization',
    title: 'Asset Utilization',
    description: 'Assets assigned vs available, assignment rates, idle assets',
    icon: TrendingUp,
    color: 'bg-green-500',
    endpoint: '/reports/utilization',
    category: 'standard',
  },
  {
    id: 'maintenance',
    title: 'Maintenance & Repairs',
    description: 'Repair statistics, costs, average repair time, top repair assets',
    icon: Wrench,
    color: 'bg-orange-500',
    endpoint: '/reports/maintenance',
    category: 'standard',
  },
  {
    id: 'employees',
    title: 'Employee Assets',
    description: 'Assets per employee, top asset holders, notorious employees',
    icon: Users,
    color: 'bg-purple-500',
    endpoint: '/reports/employees',
    category: 'standard',
  },
  {
    id: 'consumables',
    title: 'Consumables Report',
    description: 'Stock levels, low stock items, consumption rate, top issued',
    icon: Package2,
    color: 'bg-cyan-500',
    endpoint: '/reports/consumables',
    category: 'standard',
  },
  {
    id: 'documents',
    title: 'Document Report',
    description: 'Documents by type, expiring documents, documents per provider',
    icon: FileCheck,
    color: 'bg-pink-500',
    endpoint: '/reports/documents',
    category: 'standard',
  },
  // Legacy reports exposed
  {
    id: 'warranty',
    title: 'Warranty Expiry',
    description: 'Assets with expiring or expired warranties',
    icon: Shield,
    color: 'bg-amber-500',
    endpoint: '/reports/assets/warranty-expiring',
    category: 'legacy',
  },
  {
    id: 'depreciation',
    title: 'Depreciation Report',
    description: 'Asset depreciation values and book values',
    icon: TrendingUp,
    color: 'bg-red-500',
    endpoint: '/reports/assets/depreciation',
    category: 'legacy',
  },
  {
    id: 'tickets',
    title: 'Ticket Statistics',
    description: 'Support tickets by status, priority, and resolution time',
    icon: Ticket,
    color: 'bg-indigo-500',
    endpoint: '/reports/tickets/statistics',
    category: 'legacy',
  },
  {
    id: 'audit',
    title: 'Asset Audit Trail',
    description: 'History of all asset changes and movements',
    icon: History,
    color: 'bg-slate-500',
    endpoint: '/reports/assets/audit',
    category: 'legacy',
  },
];

interface ReportFilters {
  startDate: string;
  endDate: string;
  branchId: string;
  departmentId: string;
  categoryId: string;
}

export default function ReportsPage() {
  const { toast } = useToast();
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [filters, setFilters] = useState<ReportFilters>({
    startDate: '',
    endDate: '',
    branchId: '',
    departmentId: '',
    categoryId: '',
  });

  // Fetch report data
  const { data: reportData, isLoading, error: reportError, refetch, isFetching } = useQuery({
    queryKey: ['report', selectedReport?.id, filters],
    queryFn: async () => {
      if (!selectedReport) return null;
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`${selectedReport.endpoint}?${params.toString()}`);
      return response.data.data || response.data;
    },
    enabled: !!selectedReport,
  });

  // Show error toast if report fails
  if (reportError) {
    toast({ 
      title: 'Error', 
      description: 'Failed to load report data', 
      variant: 'destructive' 
    });
  }

  // Fetch branches for filter
  const { data: branches } = useQuery({
    queryKey: ['branches-list'],
    queryFn: async () => {
      const response = await api.get('/branches');
      return response.data.data || response.data;
    },
  });

  // Fetch categories for filter
  const { data: categories } = useQuery({
    queryKey: ['categories-list'],
    queryFn: async () => {
      const response = await api.get('/categories');
      return response.data.data || response.data;
    },
  });

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      branchId: '',
      departmentId: '',
      categoryId: '',
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const renderReportContent = () => {
    if (!selectedReport || !reportData) return null;

    switch (selectedReport.id) {
      case 'assets':
        return <AssetSummaryReport data={reportData} formatCurrency={formatCurrency} />;
      case 'utilization':
        return <UtilizationReport data={reportData} />;
      case 'maintenance':
        return <MaintenanceReport data={reportData} formatCurrency={formatCurrency} />;
      case 'employees':
        return <EmployeeReport data={reportData} />;
      case 'consumables':
        return <ConsumablesReport data={reportData} />;
      case 'documents':
        return <DocumentReport data={reportData} />;
      case 'warranty':
        return <WarrantyReport data={reportData} />;
      case 'depreciation':
        return <DepreciationReport data={reportData} formatCurrency={formatCurrency} />;
      case 'tickets':
        return <TicketStatsReport data={reportData} />;
      case 'audit':
        return <AuditReport data={reportData} />;
      default:
        return <div>Select a report to view</div>;
    }
  };

  const [activeTab, setActiveTab] = useState('standard');
  const [showInsights, setShowInsights] = useState(false);

  // If showing insights, render that component
  if (showInsights) {
    return <InsightReports onBack={() => setShowInsights(false)} />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Reports & Analytics</h1>
          <p className="text-muted-foreground">
            Generate comprehensive reports for assets, employees, and operations
          </p>
        </div>
        <div className="flex gap-2">
          {!selectedReport && (
            <Button onClick={() => setShowInsights(true)} className="gap-2">
              <Sparkles className="w-4 h-4" />
              Business Insights
            </Button>
          )}
          {selectedReport && (
            <ExportButton 
              reportType={selectedReport.id} 
              filters={filters as any}
              disabled={isLoading || !reportData}
            />
          )}
        </div>
      </div>

      {/* Tabs for different report sections */}
      {!selectedReport ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="standard" className="gap-2">
              <FileText className="w-4 h-4" />
              Standard
            </TabsTrigger>
            <TabsTrigger value="custom" className="gap-2">
              <PenTool className="w-4 h-4" />
              Custom Builder
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="gap-2">
              <Calendar className="w-4 h-4" />
              Scheduled
            </TabsTrigger>
            <TabsTrigger value="legacy" className="gap-2">
              <History className="w-4 h-4" />
              Additional
            </TabsTrigger>
          </TabsList>

          <TabsContent value="standard" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {reportTypes.filter(r => r.category === 'standard').map((report) => (
                <ReportCard
                  key={report.id}
                  id={report.id}
                  title={report.title}
                  description={report.description}
                  icon={report.icon}
                  color={report.color}
                  onClick={() => setSelectedReport(report)}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="custom" className="mt-6">
            <CustomReportBuilder />
          </TabsContent>

          <TabsContent value="scheduled" className="mt-6">
            <ScheduledReports />
          </TabsContent>

          <TabsContent value="legacy" className="mt-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {reportTypes.filter(r => r.category === 'legacy').map((report) => (
                <ReportCard
                  key={report.id}
                  id={report.id}
                  title={report.title}
                  description={report.description}
                  icon={report.icon}
                  color={report.color}
                  onClick={() => setSelectedReport(report)}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      ) : (
        <div className="space-y-6">
          {/* Selected Report Header */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center', selectedReport.color)}>
                  {(() => {
                    const IconComponent = selectedReport.icon;
                    return <IconComponent className="w-6 h-6 text-white" />;
                  })()}
                </div>
                <div>
                  <CardTitle>{selectedReport.title}</CardTitle>
                  <CardDescription>{selectedReport.description}</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  <RefreshCw className={cn('w-4 h-4 mr-2', isFetching && 'animate-spin')} />
                  Refresh
                </Button>
                <Button variant="outline" onClick={() => setSelectedReport(null)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Filters */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                <CardTitle className="text-lg">Filters</CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Start Date
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    End Date
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="branch" className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Branch
                  </Label>
                  <select
                    id="branch"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={filters.branchId}
                    onChange={(e) => setFilters({ ...filters, branchId: e.target.value })}
                  >
                    <option value="">All Branches</option>
                    {branches?.map((branch: any) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category" className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    Category
                  </Label>
                  <select
                    id="category"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={filters.categoryId}
                    onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
                  >
                    <option value="">All Categories</option>
                    {categories?.map((cat: any) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Content */}
          {isLoading ? (
            <Card>
              <CardContent className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </CardContent>
            </Card>
          ) : (
            renderReportContent()
          )}
        </div>
      )}
    </div>
  );
}

// ==================== Report Components ====================

function AssetSummaryReport({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  const navigate = useNavigate();
  
  const statusData = data.byStatus?.map((s: any) => ({
    name: s.status.replace('_', ' '),
    value: s.count,
  })) || [];

  const categoryData = data.byCategory?.map((c: any) => ({
    name: c.categoryName,
    value: c.count,
    purchaseValue: c.purchaseValue,
    currentValue: c.currentValue,
  })) || [];

  const branchData = data.byBranch?.map((b: any) => ({
    id: b.branchId,
    name: b.branchName,
    value: b.count,
    totalValue: b.totalValue,
  })) || [];

  // Calculate percentages for status
  const totalAssets = data.summary?.total || 0;
  const statusWithPercentage = statusData.map((s: any) => ({
    ...s,
    percentage: totalAssets > 0 ? ((s.value / totalAssets) * 100).toFixed(1) : 0
  }));

  // Get top category
  const topCategory = categoryData.length > 0 
    ? categoryData.reduce((max: any, c: any) => c.value > max.value ? c : max, categoryData[0])
    : null;

  // Get top branch
  const topBranch = branchData.length > 0
    ? branchData.reduce((max: any, b: any) => b.value > max.value ? b : max, branchData[0])
    : null;

  // Depreciation percentage
  const depreciationPercent = data.summary?.totalPurchaseValue > 0 
    ? ((data.summary?.depreciation / data.summary?.totalPurchaseValue) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards - Enhanced */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-blue-500" onClick={() => navigate('/assets')}>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Package2 className="w-4 h-4" />
              Total Assets
            </CardDescription>
            <CardTitle className="text-4xl font-bold text-blue-600">{data.summary?.total || 0}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {topCategory ? `Top: ${topCategory.name} (${topCategory.value})` : 'Click to view all'}
            </p>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Purchase Value
            </CardDescription>
            <CardTitle className="text-2xl font-bold text-green-600">{formatCurrency(data.summary?.totalPurchaseValue || 0)}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Avg: {formatCurrency(totalAssets > 0 ? (data.summary?.totalPurchaseValue || 0) / totalAssets : 0)}/asset
            </p>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Package2 className="w-4 h-4" />
              Current Value
            </CardDescription>
            <CardTitle className="text-2xl font-bold text-purple-600">{formatCurrency(data.summary?.totalCurrentValue || 0)}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {data.summary?.totalCurrentValue > 0 ? 'After depreciation' : 'Not calculated'}
            </p>
          </CardHeader>
        </Card>

        <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Total Depreciation
            </CardDescription>
            <CardTitle className="text-2xl font-bold text-red-500">{formatCurrency(data.summary?.depreciation || 0)}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {depreciationPercent}% of purchase value
            </p>
          </CardHeader>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-4 md:grid-cols-4">
        {statusWithPercentage.map((status: any) => (
          <Card 
            key={status.name} 
            className="hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(`/assets?status=${status.name.replace(' ', '_').toUpperCase()}`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{status.name}</p>
                  <p className="text-2xl font-bold">{status.value}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold text-muted-foreground">{status.percentage}%</p>
                </div>
              </div>
              <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full",
                    status.name === 'DEPLOYED' || status.name === 'ASSIGNED' ? 'bg-blue-500' :
                    status.name === 'AVAILABLE' || status.name === 'IN STOCK' ? 'bg-green-500' :
                    status.name === 'IN MAINTENANCE' || status.name === 'UNDER REPAIR' ? 'bg-orange-500' :
                    'bg-gray-500'
                  )}
                  style={{ width: `${status.percentage}%` }}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts - Enhanced Layout */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Assets by Status
            </CardTitle>
            <CardDescription>Distribution overview</CardDescription>
          </CardHeader>
          <CardContent>
            <ReportChart type="pie" data={statusData} height={250} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Assets by Category
            </CardTitle>
            <CardDescription>Click bars to filter by category</CardDescription>
          </CardHeader>
          <CardContent>
            <ReportChart type="bar" data={categoryData} height={280} title="Assets" />
          </CardContent>
        </Card>
      </div>

      {/* Tables - Enhanced with more details */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building className="w-4 h-4" />
              Assets by Branch
            </CardTitle>
            <CardDescription>
              {topBranch ? `Highest: ${topBranch.name} with ${topBranch.value} assets` : 'Asset distribution by location'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={branchData}
              columns={[
                { key: 'name', header: 'Branch' },
                { key: 'value', header: 'Count', className: 'text-right font-medium' },
                { 
                  key: 'value', 
                  header: '%', 
                  className: 'text-right text-muted-foreground',
                  render: (v) => totalAssets > 0 ? `${((v / totalAssets) * 100).toFixed(1)}%` : '0%'
                },
                { key: 'totalValue', header: 'Value', className: 'text-right', render: (v) => formatCurrency(v || 0) },
              ]}
              onRowClick={(row: any) => navigate(`/assets?branchId=${row.id}`)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package2 className="w-4 h-4" />
              Assets by Condition
            </CardTitle>
            <CardDescription>Physical condition of assets</CardDescription>
          </CardHeader>
          <CardContent>
            {data.byCondition && data.byCondition.length > 0 ? (
              <ReportTable
                data={data.byCondition}
                columns={[
                  { 
                    key: 'condition', 
                    header: 'Condition',
                    render: (v) => (
                      <span className={cn(
                        "px-2 py-1 rounded-full text-xs font-medium",
                        v === 'NEW' ? 'bg-green-100 text-green-800' :
                        v === 'GOOD' ? 'bg-blue-100 text-blue-800' :
                        v === 'FAIR' ? 'bg-yellow-100 text-yellow-800' :
                        v === 'POOR' ? 'bg-orange-100 text-orange-800' :
                        v === 'BROKEN' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      )}>
                        {v || 'Unknown'}
                      </span>
                    )
                  },
                  { key: 'count', header: 'Count', className: 'text-right font-medium' },
                  { 
                    key: 'count', 
                    header: '%', 
                    className: 'text-right text-muted-foreground',
                    render: (v) => totalAssets > 0 ? `${((v / totalAssets) * 100).toFixed(1)}%` : '0%'
                  },
                ]}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <Package2 className="w-10 h-10 mb-2 opacity-50" />
                <p>No condition data available</p>
                <p className="text-xs">Update asset conditions to see data here</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Details Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Category Value Breakdown
          </CardTitle>
          <CardDescription>Purchase and current values by category</CardDescription>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={categoryData}
            columns={[
              { key: 'name', header: 'Category' },
              { key: 'value', header: 'Count', className: 'text-right font-medium' },
              { key: 'purchaseValue', header: 'Purchase Value', className: 'text-right', render: (v) => formatCurrency(v || 0) },
              { key: 'currentValue', header: 'Current Value', className: 'text-right', render: (v) => formatCurrency(v || 0) },
              { 
                key: 'purchaseValue', 
                header: 'Depreciation', 
                className: 'text-right text-red-500',
                render: (v, row) => formatCurrency((v || 0) - (row.currentValue || 0))
              },
            ]}
            onRowClick={(row: any) => navigate(`/assets?categoryId=${row.categoryId}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function UtilizationReport({ data }: { data: any }) {
  const navigate = useNavigate();
  const utilizationData = data.utilizationByCategory?.map((c: any) => ({
    name: c.categoryName,
    value: c.utilizationRate,
    assigned: c.assigned,
    available: c.available,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Assigned</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.assignedAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Available</CardDescription>
            <CardTitle className="text-3xl text-blue-500">{data.summary?.availableAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>In Maintenance</CardDescription>
            <CardTitle className="text-3xl text-orange-500">{data.summary?.inMaintenanceAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Utilization Rate</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.overallUtilizationRate || 0}%</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Charts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Utilization Rate by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart type="horizontal-bar" data={utilizationData} height={300} title="Utilization %" />
        </CardContent>
      </Card>

      {/* Idle Assets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Idle Assets (Unassigned for 30+ days)
          </CardTitle>
          <CardDescription>{data.summary?.idleAssetCount || 0} assets identified</CardDescription>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.idleAssets || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category', header: 'Category' },
              { key: 'branch', header: 'Branch' },
              { key: 'daysSinceActivity', header: 'Days Idle', className: 'text-right' },
            ]}
            onRowClick={(row: any) => navigate(`/assets/${row.id}`)}
            emptyMessage="No idle assets found"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MaintenanceReport({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  const statusData = data.byStatus?.map((s: any) => ({
    name: s.status.replace('_', ' '),
    value: s.count,
  })) || [];

  const typeData = data.byType?.map((t: any) => ({
    name: t.type,
    value: t.count,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Repairs</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalRepairs || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>In Progress</CardDescription>
            <CardTitle className="text-3xl text-orange-500">{data.summary?.inProgressRepairs || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Cost</CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(data.summary?.totalCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Repair Days</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.avgRepairDays || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Repairs by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportChart type="pie" data={statusData} height={280} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Repairs by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportChart type="bar" data={typeData} height={280} title="Repairs" />
          </CardContent>
        </Card>
      </div>

      {/* Tables */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Top Assets Needing Repairs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.topRepairAssets || []}
              columns={[
                { key: 'assetTag', header: 'Asset Tag' },
                { key: 'name', header: 'Name' },
                { key: 'repairCount', header: 'Repairs', className: 'text-right' },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Service Providers</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.topServiceProviders || []}
              columns={[
                { key: 'providerName', header: 'Provider' },
                { key: 'repairCount', header: 'Repairs', className: 'text-right' },
                { key: 'totalCost', header: 'Total Cost', className: 'text-right', render: (v) => formatCurrency(v || 0) },
              ]}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmployeeReport({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Employees</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalEmployees || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>With Assets</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.employeesWithAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Without Assets</CardDescription>
            <CardTitle className="text-3xl text-gray-500">{data.summary?.employeesWithoutAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Assets/Employee</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.averageAssetsPerEmployee || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Tables */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Top Asset Holders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.topAssetHolders || []}
              columns={[
                { key: 'employeeId', header: 'Employee ID' },
                { key: 'name', header: 'Name' },
                { key: 'department', header: 'Department' },
                { key: 'assetCount', header: 'Assets', className: 'text-right' },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Notorious Employees (3+ Repairs)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.notoriousEmployees || []}
              columns={[
                { key: 'employeeId', header: 'Employee ID' },
                { key: 'name', header: 'Name' },
                { key: 'department', header: 'Department' },
                { key: 'repairCount', header: 'Repairs', className: 'text-right text-red-500' },
              ]}
              emptyMessage="No notorious employees found"
            />
          </CardContent>
        </Card>
      </div>

      {/* Full Employee List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Employees</CardTitle>
          <CardDescription>Complete list of employees and their asset counts</CardDescription>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.employees?.slice(0, 50) || []}
            columns={[
              { key: 'employeeId', header: 'Employee ID' },
              { key: 'name', header: 'Name' },
              { key: 'email', header: 'Email' },
              { key: 'department', header: 'Department' },
              { key: 'branch', header: 'Branch' },
              { key: 'assetCount', header: 'Assets', className: 'text-right' },
              { key: 'repairCount', header: 'Repairs', className: 'text-right' },
            ]}
            maxHeight="max-h-[400px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ConsumablesReport({ data }: { data: any }) {
  const stockByBranch = data.stockByBranch?.map((b: any) => ({
    name: b.branchName,
    value: b.totalQuantity,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Items</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalItems || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Stock</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalStockItems || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Low Stock</CardDescription>
            <CardTitle className="text-3xl text-amber-500">{data.summary?.lowStockCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Out of Stock</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.outOfStockCount || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Charts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Stock by Branch</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart type="bar" data={stockByBranch} height={280} title="Quantity" />
        </CardContent>
      </Card>

      {/* Tables */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Low Stock Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.lowStockItems || []}
              columns={[
                { key: 'name', header: 'Item' },
                { key: 'category', header: 'Category' },
                { key: 'currentStock', header: 'Current', className: 'text-right text-red-500' },
                { key: 'minimumStock', header: 'Minimum', className: 'text-right' },
              ]}
              emptyMessage="No low stock items"
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Top Issued Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.topIssuedItems || []}
              columns={[
                { key: 'name', header: 'Item' },
                { key: 'category', header: 'Category' },
                { key: 'totalIssued', header: 'Total Issued', className: 'text-right' },
              ]}
            />
          </CardContent>
        </Card>
      </div>

      {/* Recent Issues */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Issues</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.recentIssues?.slice(0, 20) || []}
            columns={[
              { key: 'itemName', header: 'Item' },
              { key: 'quantity', header: 'Qty', className: 'text-right' },
              { key: 'issuedTo', header: 'Issued To' },
              { key: 'branch', header: 'Branch' },
              { key: 'issuedAt', header: 'Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function DocumentReport({ data }: { data: any }) {
  const typeData = data.byType?.map((t: any) => ({
    name: t.type,
    value: t.count,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Documents</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalDocuments || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Expiring Soon</CardDescription>
            <CardTitle className="text-3xl text-amber-500">{data.summary?.expiringCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Expired</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.expiredCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>With Provider</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.documentsWithProvider || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Documents by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportChart type="pie" data={typeData} height={280} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Documents by Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.byProvider || []}
              columns={[
                { key: 'providerName', header: 'Provider' },
                { key: 'count', header: 'Documents', className: 'text-right' },
              ]}
            />
          </CardContent>
        </Card>
      </div>

      {/* Expiring Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Expiring Documents (Next 30 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.expiringDocuments || []}
            columns={[
              { key: 'name', header: 'Document' },
              { key: 'type', header: 'Type' },
              { key: 'provider', header: 'Provider' },
              { key: 'expiryDate', header: 'Expiry Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' },
              { 
                key: 'daysUntilExpiry', 
                header: 'Days Left', 
                className: 'text-right',
                render: (v) => (
                  <span className={cn(
                    'font-medium',
                    v <= 7 ? 'text-red-500' : v <= 14 ? 'text-amber-500' : 'text-green-500'
                  )}>
                    {v}
                  </span>
                ),
              },
            ]}
            emptyMessage="No documents expiring soon"
          />
        </CardContent>
      </Card>

      {/* Recent Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.recentDocuments || []}
            columns={[
              { key: 'name', header: 'Document' },
              { key: 'type', header: 'Type' },
              { key: 'provider', header: 'Provider' },
              { key: 'uploadedBy', header: 'Uploaded By' },
              { key: 'uploadedAt', header: 'Upload Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Legacy Report Components ====================

function WarrantyReport({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Expiring Soon</CardDescription>
            <CardTitle className="text-3xl text-amber-500">{data.expiringCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Already Expired</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.expiredCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Expiring in 30 Days</CardDescription>
            <CardTitle className="text-3xl">{data.expiringWithin30Days?.length || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Warranties Expiring Soon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.assets || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category.name', header: 'Category' },
              { key: 'brand.name', header: 'Brand' },
              { key: 'warrantyExpiry', header: 'Expiry Date', render: (v) => v ? new Date(v).toLocaleDateString() : '-' },
              { key: 'branch.name', header: 'Branch' },
            ]}
            emptyMessage="No warranties expiring soon"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Recently Expired
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.recentlyExpired || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category.name', header: 'Category' },
              { key: 'warrantyExpiry', header: 'Expired On', render: (v) => v ? new Date(v).toLocaleDateString() : '-' },
            ]}
            emptyMessage="No recently expired warranties"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function DepreciationReport({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Purchase Value</CardDescription>
            <CardTitle className="text-xl">{formatCurrency(data.summary?.totalPurchaseValue || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Depreciation</CardDescription>
            <CardTitle className="text-xl text-red-500">{formatCurrency(data.summary?.totalDepreciation || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Book Value</CardDescription>
            <CardTitle className="text-xl text-green-500">{formatCurrency(data.summary?.totalBookValue || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Fully Depreciated</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.fullyDepreciated || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Asset Depreciation Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.assets?.slice(0, 50) || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category', header: 'Category' },
              { key: 'purchaseCost', header: 'Purchase Cost', className: 'text-right', render: (v) => formatCurrency(v || 0) },
              { key: 'yearsOwned', header: 'Years Owned', className: 'text-right' },
              { key: 'totalDepreciation', header: 'Depreciation', className: 'text-right text-red-500', render: (v) => formatCurrency(v || 0) },
              { key: 'bookValue', header: 'Book Value', className: 'text-right text-green-500', render: (v) => formatCurrency(v || 0) },
              { 
                key: 'fullyDepreciated', 
                header: 'Status', 
                render: (v) => v ? (
                  <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">Fully Depreciated</span>
                ) : (
                  <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Active</span>
                )
              },
            ]}
            maxHeight="max-h-[500px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function TicketStatsReport({ data }: { data: any }) {
  const statusData = data.byStatus?.map((s: any) => ({
    name: s.status.replace('_', ' '),
    value: s.count,
  })) || [];

  const priorityData = data.byPriority?.map((p: any) => ({
    name: p.priority,
    value: p.count,
  })) || [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Tickets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.total || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Open</CardDescription>
            <CardTitle className="text-3xl text-blue-500">{data.summary?.open || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>In Progress</CardDescription>
            <CardTitle className="text-3xl text-amber-500">{data.summary?.inProgress || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Resolved</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.resolved || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>SLA Breached</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.slaBreached || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Resolution (hrs)</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.avgResolutionHours || 0}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tickets by Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportChart type="pie" data={statusData} height={280} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tickets by Priority</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportChart type="bar" data={priorityData} height={280} title="Tickets" />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AuditReport({ data }: { data: any }) {
  const actionCounts = Object.entries(data.byAction || {}).map(([action, count]) => ({
    name: action,
    value: count as number,
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Audit Entries</CardDescription>
            <CardTitle className="text-3xl">{data.total || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Action Types</CardDescription>
            <CardTitle className="text-3xl">{Object.keys(data.byAction || {}).length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart type="bar" data={actionCounts} height={300} title="Count" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Audit Trail</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.history || []}
            columns={[
              { key: 'asset.assetTag', header: 'Asset Tag' },
              { key: 'asset.name', header: 'Asset Name' },
              { key: 'action', header: 'Action' },
              { key: 'performedAt', header: 'Date', render: (v) => v ? new Date(v).toLocaleString() : '-' },
            ]}
            maxHeight="max-h-[400px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}
